import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService, Event } from '../services/api';
import TicketPurchase from './TicketPurchase';
import './EventDetail.css';

const EventDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const eventData = await apiService.getEvent(id);
        setEvent(eventData);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(t('events.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, t]);

  const shareEvent = (platform: 'facebook' | 'twitter' | 'linkedin' | 'whatsapp') => {
    if (!event) return;
    
    const url = window.location.href;
    const text = `Check out this amazing event: ${event.title}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const openMap = () => {
    if (event?.location) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(event.location)}`, '_blank');
    }
  };

  const pauseCurrentVideo = useCallback(() => {
    const currentVideo = document.querySelector('.carousel-main-image video') as HTMLVideoElement;
    if (currentVideo) {
      currentVideo.pause();
    }
  }, []);

  const nextImage = useCallback(() => {
    if (event?.medias && event.medias.length > 0) {
      pauseCurrentVideo();
      setCurrentImageIndex((prev) => (prev + 1) % event.medias!.length);
    }
  }, [event?.medias, pauseCurrentVideo]);

  const prevImage = useCallback(() => {
    if (event?.medias && event.medias.length > 0) {
      pauseCurrentVideo();
      setCurrentImageIndex((prev) => (prev - 1 + event.medias!.length) % event.medias!.length);
    }
  }, [event?.medias, pauseCurrentVideo]);

  const goToImage = (index: number) => {
    pauseCurrentVideo();
    setCurrentImageIndex(index);
  };

  // Get first image from event medias for video poster
  const getVideoPoster = useCallback(() => {
    if (event?.medias) {
      const firstImage = event.medias.find(media => media.type === 'image');
      return firstImage?.url || '/placeholder-event.svg';
    }
    return '/placeholder-event.svg';
  }, [event?.medias]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (event?.medias && event.medias.length > 1) {
        if (e.key === 'ArrowLeft') {
          prevImage();
        } else if (e.key === 'ArrowRight') {
          nextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [event?.medias, nextImage, prevImage]);

  // Auto-advance carousel every 5 seconds, but pause if video is playing
  useEffect(() => {
    if (event?.medias && event.medias.length > 1) {
      const interval = setInterval(() => {
        // Check if current media is a video and if it's playing
        if (event.medias![currentImageIndex].type === 'video') {
          const currentVideo = document.querySelector('.carousel-main-image video') as HTMLVideoElement;
          if (currentVideo && !currentVideo.paused && !currentVideo.ended) {
            // Video is playing, don't advance
            return;
          }
        }
        // Safe to advance to next image
        nextImage();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [event?.medias, currentImageIndex, nextImage]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container">
        <div className="error">{error || t('events.notFound')}</div>
        <Link to="/" className="btn btn-secondary">← Back to Events</Link>
      </div>
    );
  }

  return (
    <div className="event-detail-modern">
      {/* Main Banner Section */}
      {event.mainBanner && (
        <div className="main-banner-section">
          <div className="main-banner-container">
            {event.mainBanner.type === 'image' ? (
              <img 
                src={event.mainBanner.url} 
                alt={event.mainBanner.description || event.title}
                className="main-banner-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-event.svg';
                }}
              />
            ) : (
              <video 
                src={event.mainBanner.url} 
                className="main-banner-video"
                autoPlay
                muted
                loop
                poster='/placeholder-event.svg'
              />
            )}
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="event-header-section">
        {/* <Link to="/" className="back-link">← {t('common.back')}</Link> */}
        
        <div className="event-hero">
          <div className="event-hero-content">
            <h1 className="event-title">{event.title}</h1>
            <p className="event-subtitle">{event.description}</p>
            
            <div className="event-meta-top">
              <div className="meta-item">
                <span className="meta-icon">📅</span>
                <span>{new Date(event.startDateTime).toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="meta-item location-with-map">
                <span className="meta-icon">📍</span>
                <span>{event.location}</span>
                <button 
                  className="inline-map-btn"
                  onClick={openMap}
                  title={t('events.viewOnMap')}
                >
                  <span className="map-icon">🗺️</span>
                  {t('events.viewOnMap')}
                </button>
              </div>
            </div>
          </div>

          <div className="hero-buy-section">
            <button 
              className="hero-buy-tickets-btn"
              onClick={() => setShowPurchase(true)}
            >
              {t('events.buyTickets')}
              <span className="hero-price-range">
                {event.ticketCategories && event.ticketCategories.length > 0 
                  ? `€${Math.min(...event.ticketCategories.map(cat => cat.price)).toFixed(2)} - €${Math.max(...event.ticketCategories.map(cat => cat.price)).toFixed(2)}`
                  : t('events.checkPrices')
                }
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className={`event-main-layout ${(!event.medias || event.medias.length === 0) ? 'no-media' : ''}`}>
        {/* Left Side - Media or About Section */}
        {(event.medias && event.medias.length > 0) ? (
          <div className="event-media-section">
            <div className="event-media-carousel">
              <div className={`carousel-container ${event.medias.length === 1 ? 'single-media' : ''}`}>
                <div className="carousel-main-image">
                  {event.medias[currentImageIndex].type === 'image' ? (
                    <img 
                      src={event.medias[currentImageIndex].url} 
                      alt={event.medias[currentImageIndex].description || event.title}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-event.svg';
                      }}
                    />
                  ) : (
                    <video 
                      src={event.medias[currentImageIndex].url} 
                      controls
                      poster={getVideoPoster()}
                    />
                  )}
                  
                  {/* Navigation arrows */}
                  {event.medias.length > 1 && (
                    <>
                      <button className="carousel-nav prev" onClick={prevImage}>
                        ‹
                      </button>
                      <button className="carousel-nav next" onClick={nextImage}>
                        ›
                      </button>
                    </>
                  )}
                </div>
                
                {/* Thumbnail strip */}
                {event.medias.length > 1 && (
                  <div className="carousel-thumbnails">
                    {event.medias.map((media, index) => (
                      <div 
                        key={index}
                        className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => goToImage(index)}
                      >
                        {media.type === 'image' ? (
                          <img 
                            src={media.url} 
                            alt={media.description || `Thumbnail ${index + 1}`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-event.svg';
                            }}
                          />
                        ) : (
                          <div className="video-thumbnail">
                            <span>▶</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image counter */}
                {event.medias.length > 1 && (
                  <div className="image-counter">
                    {currentImageIndex + 1} / {event.medias.length}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="event-about-content-section">
            <div className="about-content">
              <h2>{t('events.aboutEvent')}</h2>
              <p>{event.description}</p>
            </div>
            
            <div className="sharing-section">
              <h3>{t('header.share')}</h3>
              <div className="share-buttons-grid">
                <button 
                  className="share-btn facebook-btn"
                  onClick={() => shareEvent('facebook')}
                >
                  Facebook
                </button>
                <button 
                  className="share-btn twitter-btn"
                  onClick={() => shareEvent('twitter')}
                >
                  Twitter
                </button>
                <button 
                  className="share-btn linkedin-btn"
                  onClick={() => shareEvent('linkedin')}
                >
                  LinkedIn
                </button>
                <button 
                  className="share-btn whatsapp-btn"
                  onClick={() => shareEvent('whatsapp')}
                >
                  WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Right Side - Event Info */}
        <div className="event-info-section">
          <div className="info-card">
            <h3>{t('events.eventInformation')}</h3>
            
            <div className="info-row">
              <div className="info-label">📅 {t('events.date')}</div>
              <div className="info-value">
                {new Date(event.startDateTime).toLocaleDateString('pt-BR', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">
                <span className="location-icon">📍</span>
                {t('events.location')}
              </div>
              <div className="info-value location-info">
                <span className="location-name">{event.location}</span>
                <button 
                  className="map-link-button"
                  onClick={openMap}
                  title={t('events.viewOnMap')}
                >
                  <span className="map-icon">🗺️</span>
                  {t('events.viewOnMap')}
                </button>
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">💰 {t('common.price')}</div>
              <div className="info-value price-display">
                {event.ticketCategories && event.ticketCategories.length > 0 ? (
                  <>
                    <span className="main-price">
                      {t('events.generalTicket')}: {event.ticketCategories[0].isFree ? t('events.free') : `€${event.ticketCategories[0].price.toFixed(2)}`}
                    </span>
                    {!event.hideTicketOptionsCount && event.ticketCategories.length > 1 && (
                      <span className="categories-count">
                        {event.ticketCategories.length} {t('events.optionAvailable', { count: event.ticketCategories.length })}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{t('events.checkPrices')}</span>
                )}
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">👥 {t('events.capacity')}</div>
              <div className="info-value">
                {event.ticketCategories && event.ticketCategories.some(cat => cat.maxAttendees) 
                  ? `${t('events.general')}: ${event.ticketCategories.reduce((total, cat) => total + (cat.maxAttendees || 0), 0)} ${t('events.seats')}`
                  : `${t('events.general')}: 500 ${t('events.seats')}`
                }
              </div>
            </div>

            <button 
              className="btn-purchase-main"
              onClick={() => setShowPurchase(true)}
            >
              {t('events.buyTickets')}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Section - About Event - Only when there are medias */}
      {(event.medias && event.medias.length > 0) && (
        <div className="event-about-section">
          <div className="about-content">
            <h2>{t('events.aboutEvent')}</h2>
            <p>{event.description}</p>
          </div>
          
          <div className="sharing-section">
            <h3>{t('header.share')}</h3>
            <div className="share-buttons-grid">
              <button 
                className="share-btn facebook-btn"
                onClick={() => shareEvent('facebook')}
              >
                Facebook
              </button>
              <button 
                className="share-btn twitter-btn"
                onClick={() => shareEvent('twitter')}
              >
                Twitter
              </button>
              <button 
                className="share-btn linkedin-btn"
                onClick={() => shareEvent('linkedin')}
              >
                LinkedIn
              </button>
              <button 
                className="share-btn whatsapp-btn"
                onClick={() => shareEvent('whatsapp')}
              >
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {showPurchase && (
        <div className="modal-overlay" onClick={() => setShowPurchase(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowPurchase(false)}>×</button>
            <TicketPurchase eventId={event.id} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
